import { supabase, GameScore } from "./supabase";

export class GameScoreService {
  /**
   * Inizializza un nuovo utente con score 0
   */
  static async initializeUser(
    userId: string,
    email: string,
    firstName?: string,
    lastName?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Verifica se l'utente esiste già
      const { data: existingScores, error: checkError } = await supabase
        .from("game_scores")
        .select("id")
        .eq("user_id", userId)
        .limit(1);

      if (checkError) {
        console.error("Error checking existing user:", checkError);
        return { success: false, error: checkError.message };
      }

      // Se l'utente ha già dei record, aggiorna nome e cognome se forniti
      if (existingScores && existingScores.length > 0) {
        console.log("User already initialized");

        // Aggiorna nome e cognome se forniti
        if (firstName || lastName) {
          const updateData: any = {};
          if (firstName) updateData.first_name = firstName;
          if (lastName) updateData.last_name = lastName;

          const { error: updateError } = await supabase
            .from("game_scores")
            .update(updateData)
            .eq("user_id", userId);

          if (updateError) {
            console.error("Error updating user name:", updateError);
          }
        }

        return { success: true };
      }

      // Crea il primo record con score 0
      const { error: insertError } = await supabase.from("game_scores").insert([
        {
          user_id: userId,
          email: email,
          first_name: firstName || null,
          last_name: lastName || null,
          personaggio_selezionato: 0,
          score: 0,
        },
      ]);

      if (insertError) {
        console.error("Error initializing user:", insertError);
        return { success: false, error: insertError.message };
      }

      console.log("User initialized successfully with score 0");
      return { success: true };
    } catch (error) {
      console.error("Unexpected error initializing user:", error);
      return {
        success: false,
        error: "Errore inaspettato durante l'inizializzazione",
      };
    }
  }

  /**
   * Salva lo score del giocatore nel database
   */
  static async saveScore(
    userId: string,
    email: string,
    personaggioSelezionato: number,
    score: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.from("game_scores").insert([
        {
          user_id: userId,
          email: email,
          personaggio_selezionato: personaggioSelezionato,
          score: score,
        },
      ]);

      if (error) {
        console.error("Error saving score:", error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error("Unexpected error saving score:", error);
      return {
        success: false,
        error: "Errore inaspettato durante il salvataggio",
      };
    }
  }

  /**
   * Recupera tutti gli score di un utente
   */
  static async getUserScores(userId: string): Promise<GameScore[]> {
    try {
      const { data, error } = await supabase
        .from("game_scores")
        .select("*")
        .eq("user_id", userId)
        .order("score", { ascending: false });

      if (error) {
        console.error("Error fetching user scores:", error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error("Unexpected error fetching user scores:", error);
      return [];
    }
  }

  /**
   * Recupera il miglior score di un utente
   */
  static async getUserBestScore(userId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from("game_scores")
        .select("score")
        .eq("user_id", userId)
        .order("score", { ascending: false })
        .limit(1)
        .single();

      if (error || !data) {
        return 0;
      }

      return data.score;
    } catch (error) {
      console.error("Error fetching user best score:", error);
      return 0;
    }
  }

  /**
   * Recupera la classifica generale (top 100)
   */
  static async getLeaderboard(limit: number = 100): Promise<GameScore[]> {
    try {
      const { data, error } = await supabase
        .from("game_scores")
        .select("*")
        .order("score", { ascending: false })
        .order("update", { ascending: true })
        .limit(limit);

      if (error) {
        console.error("Error fetching leaderboard:", error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error("Unexpected error fetching leaderboard:", error);
      return [];
    }
  }

  /**
   * Recupera la classifica con i migliori score per utente
   */
  static async getBestScoresLeaderboard(
    limit: number = 100
  ): Promise<GameScore[]> {
    try {
      const { data, error } = await supabase
        .from("game_scores")
        .select("*")
        .order("score", { ascending: false })
        .order("update", { ascending: true })
        .limit(limit);

      if (error) {
        console.error("Error fetching best scores leaderboard:", error);
        return [];
      }

      console.log("Loaded scores from Supabase:", data?.length || 0);
      return data || [];
    } catch (error) {
      console.error(
        "Unexpected error fetching best scores leaderboard:",
        error
      );
      return [];
    }
  }

  /**
   * Recupera la posizione e il miglior score di un utente nella classifica
   */
  static async getUserRankingPosition(
    userId: string
  ): Promise<{ position: number; score: number; email: string } | null> {
    try {
      const { data: userBestScores, error: userError } = await supabase
        .from("game_scores")
        .select("score, email, update")
        .eq("user_id", userId)
        .order("score", { ascending: false })
        .order("update", { ascending: true })
        .limit(1);

      if (userError || !userBestScores || userBestScores.length === 0) {
        return null;
      }

      const userBestScore = userBestScores[0];

      const { count: betterCount, error: betterError } = await supabase
        .from("game_scores")
        .select("*", { count: "exact", head: true })
        .gt("score", userBestScore.score);

      if (betterError) {
        console.error("Error counting better scores:", betterError);
        return null;
      }

      const { count: sameScoreEarlierCount, error: sameScoreError } =
        await supabase
          .from("game_scores")
          .select("*", { count: "exact", head: true })
          .eq("score", userBestScore.score)
          .lt("update", userBestScore.update);

      if (sameScoreError) {
        console.error("Error counting same score earlier:", sameScoreError);
        return null;
      }

      const position = (betterCount || 0) + (sameScoreEarlierCount || 0) + 1;

      return {
        position: position,
        score: userBestScore.score,
        email: userBestScore.email,
      };
    } catch (error) {
      console.error("Error fetching user ranking position:", error);
      return null;
    }
  }
}
